import { createContext, useContext } from 'react';
import type { ReactNode, ComponentType } from 'react';

import type { PageProps } from '@shared/types/page-props';

export type PagePropsContextType<T = unknown> = T;

export const PagePropsContext = createContext<PagePropsContextType>({});

export const PagePropsProvider = ({
  children,
  value,
}: {
  value: PageProps & { isProductionEnv: boolean };
  children: ReactNode;
}) => (
  <PagePropsContext.Provider value={value}>
    {children}
  </PagePropsContext.Provider>
);

export const usePageProps = <T,>() => useContext(PagePropsContext) as T;

export const withPageProps =
  <T,>(Component: ComponentType<T>) =>
  (props: Omit<T, keyof PagePropsContextType>) => {
    const pageProps = usePageProps<PagePropsContextType>();
    // @ts-expect-error we expect that pageProps can be overwritten in the unit tests
    return <Component {...(props as T)} {...pageProps} />;
  };

export const useIsProductionEnv = () => {
  const { isProductionEnv } = usePageProps<{ isProductionEnv: boolean }>();
  return isProductionEnv;
};
